import { PlayerError } from '@foundation-player/error-codes';

/**
 * This will parse the error message from the browser and return a foundation error code.
 *
 * @remarks
 * Error messages for video playback are not standardized across browsers.
 * Basically every vendor has their own error messages, even if they use the same engine.
 *
 * We try to map the error messages on best effort basis, but this is not guaranteed to be complete.
 *
 * @param message - The message from the browser error.
 */
export function parseBrowserError(message: string): number {
  // This error is thrown when play() is called on a video element,
  // and autoplay is not allowed by the browser, due to the user not having interacted with the page yet.
  if (
    message.includes('The request is not allowed by the user agent or the platform') || // as observed in e.g. Safari 16.6
    message.includes('play() can only be initiated by a user gesture') || // as observed in e.g. Edge 120
    message.includes('The operation is not supported') || // as observed in e.g. Safari 16.1
    message.includes('The play() request was interrupted because a pause was requested by the browser') || // as observed in e.g. Android 10
    message.includes('The play method is not allowed by the user agent') || // as observed in e.g. Chrome 120
    message.includes(`play() failed because the user didn't interact with the document first`) // as observed in e.g. Android 14
  ) {
    return PlayerError.MISSING_USER_INTERACTION_35310.code;
  }

  // This error is thrown when an action is performed that is superseded by a new action.
  // This may be the case for seek during another seek, or pause after pause, or destroy during play, pause or seek etc.
  if (
    message.includes('The operation was aborted') // as observed in e.g. iOS 17
  ) {
    return PlayerError.OPERATION_ABORTED_35400.code;
  }

  // This error is thrown when an action is performed that is superseded by a new action for everything related to play().
  if (
    message.includes('The play() request was interrupted by a call to pause()') || // as observed in e.g. Chrome 120
    message.includes('The play() request was interrupted because a pause was requested by the user') // as observed in e.g. Edge 120
  ) {
    return PlayerError.OPERATION_ABORTED_PLAY_35410.code;
  }

  // This error is thrown when a new segment is loaded but the user seeked or paused playback or started something new.
  if (
    message.includes(
      `The fetching process for the media resource was aborted by the user agent at the user's request`,
    ) || // as observed in e.g. Firefox 121
    message.includes(`The play() request was interrupted by a new load request`) // as observed in e.g. Firefox 121
  ) {
    return PlayerError.OPERATION_ABORTED_LOAD_35420.code;
  }

  // This error is thrown when you lock the device when the browser was open or it goes into power saving mode
  if (
    message.includes(`The play() request was interrupted because video-only background media was paused to save power`) // as observed in e.g. Android 10
  ) {
    return PlayerError.OPERATION_ABORTED_POWER_SAVING_35430.code;
  }

  // This error is thrown when the video element is removed from the DOM, while something is playing or should play.
  if (
    message.includes(`The play() request was interrupted because the media was removed from the document`) // as observed in e.g. Android 10
  ) {
    return PlayerError.OPERATION_ABORTED_VIDEO_REMOVED_35440.code;
  }

  return PlayerError.ERROR_NOT_TREATED_59000.code;
}
